import * as React from "react"
import type { ConsumerInfoType } from "@/types"
import { useQuery } from "@tanstack/react-query"
import { createFileRoute } from "@tanstack/react-router"

import { ConsumptionHistoryChartType } from "@/types/billing"
import { getConsumptionHistory } from "@/lib/api/analytics"
import { getConsumer } from "@/lib/api/billing"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Skeleton } from "@/components/ui/skeleton"
import { BarChartMonthlyComponent } from "@/components/charts/barchart-monthly"
import { Header } from "@/components/header"

export const Route = createFileRoute(
  "/_home/manage-consumers/view-consumption-history/$id",
)({
  loader: async ({ params: { id } }) => {
    return { id }
  },
  component: ViewConsumerPage,
})

function ViewConsumerPage() {
  const { id } = Route.useLoaderData()
  const [selectedYear, setSelectedYear] = React.useState<string>(
    new Date().getFullYear().toString(),
  )

  const { data: consumerInfo, isLoading: isConsumerLoading } =
    useQuery<ConsumerInfoType>({
      queryKey: ["consumerInfo", id],
      queryFn: async () => {
        const consumer = await getConsumer(Number(id))
        return { ...consumer, id }
      },
    })

  const {
    data: chartData,
    isLoading: isChartLoading,
    isError,
  } = useQuery<ConsumptionHistoryChartType[]>({
    queryKey: ["consumptionHistory", id, selectedYear],
    queryFn: async () => {
      try {
        const res = await getConsumptionHistory(
          Number(id),
          Number(selectedYear),
        )
        return res
      } catch (error) {
        console.error("Error fetching consumptionHistory:", error)
        return []
      }
    },
    retry: false,
    staleTime: 1000 * 60 * 5,
  })

  const currentYear = new Date().getFullYear()
  const years = Array.from({ length: currentYear - 1999 }, (_, i) =>
    (currentYear - i).toString(),
  )

  const handleYearChange = (year: string) => {
    setSelectedYear(year)
  }

  return (
    <>
      <main className="space-y-4 p-4">
        <Header
          subroutes={[
            {
              route: "/manage-consumers",
              label: "Manage Consumers",
            },
          ]}
        >
          {`Consumption History (${consumerInfo?.full_name})` || "N/A"}
        </Header>

        {isError ? (
          <p className="text-center text-red-500">
            Failed to load consumption history.
          </p>
        ) : (
          <>
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold">Monthly Consumption</h2>
              <p className="text-lg font-semibold">
                January - December {selectedYear}
              </p>
              <Select
                onValueChange={handleYearChange}
                defaultValue={selectedYear}
              >
                <SelectTrigger className="w-32">
                  <SelectValue placeholder="Select Year" />
                </SelectTrigger>
                <SelectContent>
                  {years.map((year) => (
                    <SelectItem key={year} value={year}>
                      {year}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="h-auto w-full">
              {isChartLoading ? (
                <Skeleton className="h-80 w-full" />
              ) : (
                <BarChartMonthlyComponent
                  data={chartData}
                  xAxisKey="month"
                  yAxisLabel="Consumption (m3)"
                />
              )}
            </div>
          </>
        )}
      </main>
    </>
  )
}
